import { Injectable } from '@angular/core';
import { Firestore, addDoc, collection, collectionData, doc, docData, deleteDoc, updateDoc, DocumentReference, setDoc, query, where, CollectionReference, DocumentData} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { SharedService } from 'src/app/shared/shared.service';
import { TabsService } from '../../tabs.service';



@Injectable({
  providedIn: 'root'
})
export class CreateOrganisationHierarchyService {
	organisationsCollection: CollectionReference<DocumentData>;
	usersCollection: CollectionReference<DocumentData>;



	constructor(
		private firestore: Firestore,
		private sharedService: SharedService,
		private tabsService: TabsService,
	) {
		this.organisationsCollection = collection(this.firestore, "organisations");
		this.usersCollection = collection(this.firestore, "users");
	}

	async getOrgTemplate() {
		const orgId = this.tabsService.organisationId$.getValue();

		if (orgId) {
			const docRef = doc(this.firestore, `organisations/${orgId}`);
			const obs: any = docData(docRef, { idField: 'id' });
			const data = await this.sharedService.convertObsToValue(obs);
			return data || [];
		}
	}

	getOrgTemplateObs() {
		const orgId = this.tabsService.organisationId$.getValue();

		if (orgId) {
			const docRef = doc(this.firestore, `organisations/${orgId}`);
			return docData(docRef, { idField: 'id' });
		}
		return new Observable();
	}

	sanitizeHierarchy(organisationHierarchy: any) {
		const hierarchyClone = JSON.parse(JSON.stringify(organisationHierarchy || []));

		for (let level of hierarchyClone) {
			level.values = level.values || [];
			for (let val of level.values) {
				delete val['orgIndex'];
				delete val['showManagersList'];
				delete val['managerNames'];
				delete val['hasLoadedManagers'];
			}
		}

		return hierarchyClone;
	}

	isSameHierarchyValue(first: any, second: any) {
		return String(first?.value || "") === String(second?.value || "")
			&& this.sharedService.areArraysEqual(first?.levels || [], second?.levels || []);
	}

	ensureHierarchyLevel(hierarchy: any[], levelIndex: number, levelName: string) {
		if (!hierarchy[levelIndex]) {
			hierarchy[levelIndex] = {
				name: levelName,
				values: []
			};
		}

		if (!hierarchy[levelIndex].name) {
			hierarchy[levelIndex].name = levelName;
		}

		if (!Array.isArray(hierarchy[levelIndex].values)) {
			hierarchy[levelIndex].values = [];
		}
	}

	doesPathStartWith(path: any[], prefix: any[]) {
		if (prefix.length > path.length) {
			return false;
		}

		for (let i = 0; i < prefix.length; i += 1) {
			if (String(path[i]) !== String(prefix[i])) {
				return false;
			}
		}

		return true;
	}

	async saveOrganisationHierarchy(organisationId: string, organisationHierarchy: any) {
		try {
			const organisationRef = doc(this.firestore, `organisations/${organisationId}`);
			const hierarchyClone = this.sanitizeHierarchy(organisationHierarchy);

			await updateDoc(organisationRef, {
				hierarchy: hierarchyClone
			});
		} catch (err) {
			console.log('Unable to save organisation hierarchy.', err);
		}
	}

	async getOrganisationHierarchy(organisationId: string) {
		try {
			const orgDocRef = doc(this.firestore, `organisations/${organisationId}`);
			const orgDataObs = docData(orgDocRef, {
				idField: 'id'
			});
			const orgData: any = await this.sharedService.getObservableRes(orgDataObs);

			return orgData?.hierarchy ?? [];
		} catch (err) {
			console.log('Unable to get organisation id.', err);
		}
	}

	async mergeOrganisationHierarchy(organisationId: string, organisationHierarchy: any) {
		try {
			const latestHierarchy = this.sanitizeHierarchy(await this.getOrganisationHierarchy(organisationId));
			const incomingHierarchy = this.sanitizeHierarchy(organisationHierarchy);

			for (let i = 0; i < incomingHierarchy.length; i += 1) {
				const incomingLevel = incomingHierarchy[i];
				if (!incomingLevel) {
					continue;
				}

				this.ensureHierarchyLevel(latestHierarchy, i, incomingLevel.name);
				for (let val of incomingLevel.values || []) {
					const exists = latestHierarchy[i].values.some((existingVal: any) => this.isSameHierarchyValue(existingVal, val));
					if (!exists) {
						latestHierarchy[i].values.push(val);
					}
				}
			}

			await this.saveOrganisationHierarchy(organisationId, latestHierarchy);
			return latestHierarchy;
		} catch (err) {
			console.log('Unable to merge organisation hierarchy.', err);
			return organisationHierarchy;
		}
	}

	async addHierarchyValue(organisationId: string, levelIndex: number, levelName: string, valueData: any) {
		try {
			const latestHierarchy = this.sanitizeHierarchy(await this.getOrganisationHierarchy(organisationId));
			this.ensureHierarchyLevel(latestHierarchy, levelIndex, levelName);

			const normalizedValue = this.sanitizeHierarchy([{ values: [valueData] }])[0].values[0];
			const exists = latestHierarchy[levelIndex].values.some((existingVal: any) => this.isSameHierarchyValue(existingVal, normalizedValue));

			if (!exists) {
				latestHierarchy[levelIndex].values.push(normalizedValue);
				await this.saveOrganisationHierarchy(organisationId, latestHierarchy);
			}

			return latestHierarchy;
		} catch (err) {
			console.log('Unable to add hierarchy value.', err);
			return this.getOrganisationHierarchy(organisationId);
		}
	}

	async deleteHierarchyBranch(organisationId: string, deletedPath: any[]) {
		try {
			const latestHierarchy = this.sanitizeHierarchy(await this.getOrganisationHierarchy(organisationId));

			for (let i = 0; i < latestHierarchy.length; i += 1) {
				const level = latestHierarchy[i];
				level.values = (level.values || []).filter((val: any) => {
					const fullPath = [...(val.levels || []), val.value];
					return !this.doesPathStartWith(fullPath, deletedPath);
				});
			}

			await this.saveOrganisationHierarchy(organisationId, latestHierarchy);
			return latestHierarchy;
		} catch (err) {
			console.log('Unable to delete hierarchy branch.', err);
			return this.getOrganisationHierarchy(organisationId);
		}
	}

	async getUsersForLevel(levelName: string, organisationId: string) {
		try {
			const usersQuery = query(this.usersCollection, where("level.name", "==", levelName), where("organisationId", "==", organisationId));
			const usersDataObs = collectionData(usersQuery, {
				idField: 'id'
			});

			return await this.sharedService.getObservableRes(usersDataObs);
		} catch (err) {
			console.log('Unable to get users for level: ', levelName);
		}
	}

	async getUsersAtLevel(levels: any, organisationId: string) {
		// console.log('levels',levels);
		// console.log('organisationId',organisationId);
		try {
			const usersQuery = query(this.usersCollection, where("organisationId", "==", organisationId));
			const usersDataObs = collectionData(usersQuery, {
				idField: 'id'
			});
			let users = [];

			const usersInOrg: any = await this.sharedService.getObservableRes(usersDataObs);
			// console.log(usersInOrg);
			// console.log(levels);
			for (let user of usersInOrg) {
				const userLevels = user.level;
				let onLevel: boolean = true;
				// console.log(onLevel);
				// console.log('For user ', user.name);
				// console.log(userLevels);

				if (levels.length === userLevels.length) {
					for (var i = 0; i < levels.length; i += 1) {
						let level = levels[i];
						// console.log(level.name, userLevels[i].name);

						if (!userLevels[i] || level.name !== userLevels[i].name || level.value !== userLevels[i].value) {
							// console.log('setting to false');
							onLevel = false;
							// console.log('onLevel', onLevel);
							break;
						}
					}

					if (userLevels[i] && userLevels[i].name && userLevels[i].value) {
						onLevel = false;
						// console.log('onLevel', onLevel);
					}
					if (onLevel) {
						// console.log('onLevel', onLevel);
						users.push(user);
					}
				}
			}
			// console.log('users-----',users);
			return users;
		} catch (err) {
			console.log('Unable to get users at level', err);
		}
	}
}
